import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { cn } from '../lib/utils';
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  Receipt,
  AppWindow,
  Shield,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Globe,
} from 'lucide-react';

const navItems = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    roles: null, // all users
  },
  {
    label: 'Structures',
    path: '/structures',
    icon: Building2,
    roles: ['admin_structure', 'super_admin'],
  },
  {
    label: 'Utilisateurs',
    path: '/users',
    icon: Users,
    roles: ['admin_structure', 'super_admin'],
  },
  {
    label: 'Abonnements',
    path: '/subscriptions',
    icon: CreditCard,
    roles: ['admin_structure', 'super_admin'],
  },
  {
    label: 'Facturation',
    path: '/billing',
    icon: Receipt,
    roles: ['admin_structure', 'super_admin'],
  },
  {
    label: 'Catalogue Apps',
    path: '/apps',
    icon: AppWindow,
    roles: null,
  },
  {
    label: 'SuperAdmin',
    path: '/superadmin',
    icon: Shield,
    roles: ['super_admin'],
  },
  {
    label: 'Parametres',
    path: '/settings',
    icon: Settings,
    roles: null,
  },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout, hasNexusRole } = useAuth();
  const { lang, setLang } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredNav = navItems.filter((item) => {
    if (!item.roles) return true;
    return item.roles.some((role) => hasNexusRole(role));
  });

  const userInitials = user
    ? `${(user.prenom || user.first_name || '')[0] || ''}${(user.nom || user.last_name || '')[0] || ''}`.toUpperCase() || 'U'
    : 'U';

  const userName = user
    ? `${user.prenom || user.first_name || ''} ${user.nom || user.last_name || ''}`.trim() || user.email
    : '';

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-200">
        <div className="w-9 h-9 rounded-lg bg-nexus-600 flex items-center justify-center">
          <span className="text-white font-bold text-lg">N</span>
        </div>
        <div>
          <h1 className="text-lg font-bold text-slate-900 leading-tight">Nexus-360</h1>
          <p className="text-xs text-slate-500">Plateforme de gestion</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
                    isActive
                      ? 'bg-nexus-50 text-nexus-700 shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  )}
                >
                  <Icon className={cn('w-5 h-5 flex-shrink-0', isActive ? 'text-nexus-600' : 'text-slate-400')} />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto text-nexus-400" />}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User section at bottom */}
      <div className="border-t border-slate-200 p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-nexus-100 flex items-center justify-center">
            <span className="text-sm font-semibold text-nexus-700">{userInitials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{userName}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email || ''}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl transform transition-transform duration-200 ease-in-out lg:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <SidebarContent />
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:w-72 lg:flex-col bg-white border-r border-slate-200">
        <SidebarContent />
      </aside>

      {/* Main content area */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            {/* Mobile hamburger */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Page title area */}
            <div className="flex-1 lg:flex lg:items-center">
              <h2 className="text-lg font-semibold text-slate-900 hidden lg:block">
                {filteredNav.find((n) => n.path === location.pathname)?.label || 'Nexus-360'}
              </h2>
            </div>

            {/* Right side: lang + user + logout */}
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-sm text-slate-600">{userName}</span>

              {/* Language selector */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center gap-1.5 px-2 py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                  aria-label="Changer de langue"
                >
                  <Globe className="w-4 h-4" />
                  <span className="uppercase font-medium text-xs">{lang}</span>
                </button>
                {langOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[150px] z-50">
                    <button
                      onClick={() => { setLang('fr'); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors flex items-center gap-2 ${
                        lang === 'fr' ? 'font-semibold text-nexus-600' : 'text-slate-700'
                      }`}
                    >
                      <span>🇫🇷</span> Français
                    </button>
                    <button
                      onClick={() => { setLang('en'); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors flex items-center gap-2 ${
                        lang === 'en' ? 'font-semibold text-nexus-600' : 'text-slate-700'
                      }`}
                    >
                      <span>🇬🇧</span> English
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Deconnexion</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
